import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Clock, Calendar } from 'lucide-react';
import { Button } from './ui/button';
import { smartRatingsAPI } from '../api';
import { useSettings } from '../contexts/SettingsContext';

const MovieCard = ({ movie, showPresale = false }) => {
  const { settings } = useSettings();
  const [smartRatingOptions, setSmartRatingOptions] = useState([]);
  
  useEffect(() => {
    loadSmartRatings();
  }, []);
  
  const loadSmartRatings = async () => {
    try {
      const response = await smartRatingsAPI.getAll();
      setSmartRatingOptions(response.data);
    } catch (error) {
      console.error('Error loading smart ratings:', error);
    }
  };
  
  const getSmartRatingDetails = (ratingIdOrLabel) => {
    // First try to find by ID
    let rating = smartRatingOptions.find(r => r._id === ratingIdOrLabel);
    
    // If not found, try to find by label (backward compatibility)
    if (!rating) {
      rating = smartRatingOptions.find(r => r.label === ratingIdOrLabel);
    }
    
    return rating;
  };

  // Calculate days until release date
  const getDaysUntilRelease = (releaseDate) => {
    if (!releaseDate) return null;
    
    try {
      const today = new Date();
      let release;
      
      // Handle Turkish date format: "7 Kasım 2025"
      const turkishMonths = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 
                           'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];
      
      if (turkishMonths.some(month => releaseDate.includes(month))) {
        // Convert Turkish months to English
        const turkishToEnglish = {
          'Ocak': 'January', 'Şubat': 'February', 'Mart': 'March',
          'Nisan': 'April', 'Mayıs': 'May', 'Haziran': 'June',
          'Temmuz': 'July', 'Ağustos': 'August', 'Eylül': 'September',
          'Ekim': 'October', 'Kasım': 'November', 'Aralık': 'December'
        };
        
        let englishDate = releaseDate;
        Object.keys(turkishToEnglish).forEach(turkishMonth => {
          englishDate = englishDate.replace(turkishMonth, turkishToEnglish[turkishMonth]);
        });
        
        release = new Date(englishDate);
      } else {
        release = new Date(releaseDate);
      }
      
      // Check if date is valid
      if (isNaN(release.getTime())) {
        return null;
      }
      
      // Remove time part for accurate day calculation
      today.setHours(0, 0, 0, 0);
      release.setHours(0, 0, 0, 0);
      
      const diffTime = release - today;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      return diffDays;
    } catch (error) {
      console.error('Date calculation error:', error);
      return null;
    }
  };
  
  // Format date to DD/MM/YYYY format
  const formatDate = (dateString) => {
    if (!dateString) return '';
    
    try {
      let date;
      
      // Try to parse different formats
      if (dateString.includes('-')) {
        // YYYY-MM-DD format
        const parts = dateString.split('-');
        date = new Date(parts[0], parts[1] - 1, parts[2]);
      } else if (dateString.includes('/')) {
        // DD/MM/YYYY or MM/DD/YYYY format - assume DD/MM/YYYY
        const parts = dateString.split('/');
        if (parts[2] && parts[2].length === 4) {
          // DD/MM/YYYY
          date = new Date(parts[2], parts[1] - 1, parts[0]);
        } else {
          date = new Date(dateString);
        }
      } else {
        date = new Date(dateString);
      }
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return dateString; // Return original if parsing fails
      }
      
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      
      return `${day}/${month}/${year}`;
    } catch (error) {
      console.error('Date formatting error:', error);
      return dateString; // Return original if error
    }
  };
  
  const movieLink = movie.slug ? `/film/${movie.slug}` : `/movies/${movie._id}`;
  
  return (
    <Link to={movieLink} className="block">
      <div 
        className="group relative rounded-xl overflow-hidden transition-all hover:scale-105 hover:shadow-2xl duration-300"
        style={{
          background: `linear-gradient(to bottom right, ${settings?.gradient_movie_from || '#3b82f6'}33, ${settings?.gradient_movie_to || '#8b5cf6'}99)`,
          borderColor: `${settings?.gradient_movie_from || '#3b82f6'}4d`,
          borderWidth: '1px',
          borderStyle: 'solid'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = `linear-gradient(to bottom right, ${settings?.gradient_movie_from || '#3b82f6'}4d, ${settings?.gradient_movie_to || '#8b5cf6'}99)`;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = `linear-gradient(to bottom right, ${settings?.gradient_movie_from || '#3b82f6'}33, ${settings?.gradient_movie_to || '#8b5cf6'}99)`;
        }}
      >
        <div className="relative aspect-[2/3] overflow-hidden">
          <img
            src={movie.poster}
            alt={movie.title}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          
          {showPresale && movie.presale && (
            <div className="absolute top-4 right-4">
              <Button className="bg-[#FDB714] hover:bg-[#e5a510] text-black font-semibold px-4 py-1 text-sm rounded-full">
                Ön Satış
              </Button>
            </div>
          )}
        </div>
        
        <div className="p-4">
          <h3 className="text-white font-bold text-lg mb-3 line-clamp-2">{movie.title}</h3>
          
          {/* Duration and Genres Row */}
          <div className="flex items-center gap-2 mb-3 text-gray-400 text-sm flex-wrap">
            <div className="flex items-center gap-1">
              <Clock size={16} />
              <span>{movie.duration} dk.</span>
            </div>
            
            {movie.genres && movie.genres.slice(0, 2).map((genre, index) => (
              <span
                key={index}
                className="text-xs text-gray-400 bg-gray-800 px-2 py-1 rounded"
              >
                {genre}
              </span>
            ))}
          </div>
          
          {/* Formats */}
          {movie.formats && movie.formats.length > 0 && (
            <div className="flex gap-1.5 mb-3 flex-wrap">
              {movie.formats.slice(0, 3).map((format, index) => (
                <span
                  key={index}
                  className="text-xs text-white bg-gray-700/80 px-2 py-1 rounded"
                >
                  {format}
                </span>
              ))}
            </div>
          )}
          
          {/* Release Date + Countdown + Smart Ratings - Single Row */}
          <div className="flex items-center justify-between gap-2 flex-wrap">
            {/* Left: Release Date */}
            <div className="flex items-center gap-1.5 text-gray-400 text-xs">
              {movie.release_date && (
                <>
                  <Calendar size={14} className="text-gray-500 flex-shrink-0" />
                  <span className="font-semibold whitespace-nowrap">Vizyon:</span>
                  <span className="truncate">{formatDate(movie.release_date)}</span>
                </>
              )}
            </div>
            
            {/* Right: Countdown + Smart Ratings */}
            <div className="flex items-center gap-2">
              {(() => {
                const daysLeft = getDaysUntilRelease(movie.release_date);
                if (daysLeft > 0) {
                  return (
                    <div className="bg-white text-gray-900 px-2.5 py-0.5 rounded-full text-xs font-bold shadow-lg border border-gray-200 whitespace-nowrap">
                      SON {daysLeft} GÜN!
                    </div>
                  );
                } else if (daysLeft === 0) {
                  return (
                    <div className="bg-green-500 text-white px-2.5 py-0.5 rounded-full text-xs font-bold shadow-lg whitespace-nowrap">
                      BUGÜN VİZYONDA!
                    </div>
                  );
                }
                return null;
              })()}
              
              {/* Smart Ratings */}
              {movie.smartRatings && movie.smartRatings.length > 0 && smartRatingOptions.length > 0 && (
                <div className="flex gap-1">
                  {movie.smartRatings.slice(0, 3).map((ratingIdOrLabel, index) => {
                    const ratingDetails = getSmartRatingDetails(ratingIdOrLabel);
                    if (!ratingDetails) return null;
                    
                    return (
                      <div 
                        key={index}
                        className="group/tooltip relative"
                      >
                        <div className="w-6 h-6 bg-white/90 rounded-lg p-0.5 shadow-md hover:shadow-lg transition-all">
                          <img 
                            src={ratingDetails.icon} 
                            alt={ratingDetails.label}
                            className="w-full h-full object-contain"
                          />
                        </div>
                        {/* Tooltip */}
                        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-black/90 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover/tooltip:opacity-100 transition-opacity pointer-events-none z-10">
                          {ratingDetails.label}
                          <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1 border-3 border-transparent border-t-black/90"></div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
};

export default MovieCard;
